import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { PremiumOverlay } from '../../components/ui/premium-overlay';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { formatCurrency } from '../../lib/i18n';
import {
  Plus,
  Search,
  UserCircle,
  Phone,
  Briefcase,
  Users,
  DollarSign,
  UserX,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Wallet,
  CheckCircle2,
  Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  mechanic: { en: 'Mechanic', bn: 'মেকানিক' },
  helper: { en: 'Helper', bn: 'হেল্পার' },
  manager: { en: 'Manager', bn: 'ম্যানেজার' },
  electrician: { en: 'Electrician', bn: 'ইলেকট্রিশিয়ান' },
  painter: { en: 'Painter', bn: 'পেইন্টার' },
  cleaner: { en: 'Cleaner', bn: 'ক্লিনার' },
  accountant: { en: 'Accountant', bn: 'হিসাবরক্ষক' },
  other: { en: 'Other', bn: 'অন্যান্য' },
};

export default function EmployeesListPage() {
  const { garageSlug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { isPremium } = useAuth();
  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Month/Year filter - default to current month
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth()); // 0-indexed

  const MONTH_NAMES_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const MONTH_NAMES_BN = ['জানুয়ারি', 'ফেব্রুয়ারি', 'মার্চ', 'এপ্রিল', 'মে', 'জুন', 'জুলাই', 'আগস্ট', 'সেপ্টেম্বর', 'অক্টোবর', 'নভেম্বর', 'ডিসেম্বর'];

  const periodMonth = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;

  const fetchEmployees = useCallback(async (searchTerm = '') => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      params.set('period_month', periodMonth);
      const res = await axios.get(`${API}/employees?${params.toString()}`);
      setEmployees(res.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setLoading(false);
    }
  }, [periodMonth]);

  useEffect(() => {
    setLoading(true);
    fetchEmployees(search);
  }, [periodMonth]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchEmployees(search);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const goToPrevMonth = () => {
    if (selectedMonth === 0) { setSelectedMonth(11); setSelectedYear(y => y - 1); }
    else setSelectedMonth(m => m - 1);
  };
  const goToNextMonth = () => {
    if (selectedMonth === 11) { setSelectedMonth(0); setSelectedYear(y => y + 1); }
    else setSelectedMonth(m => m + 1);
  };
  const goToCurrentMonth = () => {
    setSelectedYear(now.getFullYear());
    setSelectedMonth(now.getMonth());
  };
  const isCurrentMonth = selectedYear === now.getFullYear() && selectedMonth === now.getMonth();

  const activeCount = employees.filter(e => e.is_active).length;
  const totalSalary = employees.filter(e => e.is_active).reduce((sum, e) => sum + (e.monthly_salary || 0), 0);
  const totalAdvances = employees.filter(e => e.is_active).reduce((sum, e) => sum + (e.total_advance_this_month || 0), 0);
  const totalSalaryPaid = employees.filter(e => e.is_active).reduce((sum, e) => sum + (e.salary_paid || 0), 0);
  const totalDisbursed = totalAdvances + totalSalaryPaid;
  const totalRemaining = employees.filter(e => e.is_active).reduce((sum, e) => sum + (e.remaining_salary || 0), 0);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <PremiumOverlay feature={language === 'en' ? 'Employee & Payroll Management' : 'কর্মচারী ও বেতন ব্যবস্থাপনা'} locked={!isPremium}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Barlow_Condensed'] flex items-center gap-2">
              <Users className="w-6 h-6 text-blue-800" />
              {language === 'en' ? 'Employees' : 'কর্মচারী'}
              <Badge className="bg-blue-100 text-blue-800 ml-2">{employees.length}</Badge>
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {language === 'en'
                ? `${activeCount} active • Monthly payroll: ${formatCurrency(totalSalary, language)}`
                : `${activeCount} সক্রিয় • মাসিক বেতন: ${formatCurrency(totalSalary, language)}`}
            </p>
          </div>
          <Link to={`${basePath}/employees/new`}>
            <Button className="bg-blue-800 hover:bg-blue-900" data-testid="new-employee-btn">
              <Plus className="w-4 h-4 mr-2" />
              {language === 'en' ? 'Add Employee' : 'কর্মচারী যোগ করুন'}
            </Button>
          </Link>
        </div>

        {/* Month/Year Picker */}
        <Card className="mb-4">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={goToPrevMonth} className="h-8 w-8 p-0">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-600" />
                <span className="font-semibold text-slate-800">
                  {language === 'en' ? MONTH_NAMES_EN[selectedMonth] : MONTH_NAMES_BN[selectedMonth]} {selectedYear}
                </span>
                {!isCurrentMonth && (
                  <Button variant="outline" size="sm" onClick={goToCurrentMonth} className="h-6 text-[10px] px-2 ml-1">
                    {language === 'en' ? 'Today' : 'আজকে'}
                  </Button>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={goToNextMonth} className="h-8 w-8 p-0">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Payroll Summary Cards */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-blue-600 font-medium">{language === 'en' ? 'Total Payroll' : 'মোট বেতন'}</span>
              </div>
              <p className="text-lg font-bold text-blue-800">{formatCurrency(totalSalary, language)}</p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span className="text-xs text-emerald-600 font-medium">{language === 'en' ? 'Total Paid' : 'মোট প্রদান'}</span>
              </div>
              <p className="text-lg font-bold text-emerald-800">{formatCurrency(totalDisbursed, language)}</p>
              {totalAdvances > 0 && (
                <p className="text-xs text-amber-600 mt-0.5">
                  {language === 'en' ? `incl. ৳${totalAdvances.toLocaleString()} advance` : `অগ্রিম ৳${totalAdvances.toLocaleString()} সহ`}
                </p>
              )}
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-red-600" />
                <span className="text-xs text-red-600 font-medium">{language === 'en' ? 'Remaining' : 'বাকি'}</span>
              </div>
              <p className="text-lg font-bold text-red-800">{formatCurrency(totalRemaining, language)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder={language === 'en' ? 'Search by name, phone, or role...' : 'নাম, ফোন বা পদবী দিয়ে খুঁজুন...'}
                className="pl-10"
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="employee-search"
              />
            </div>
          </CardContent>
        </Card>

        {/* Employee Grid */}
        {employees.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {employees.map(emp => (
              <Link key={emp.id} to={`${basePath}/employees/${emp.id}`} data-testid={`employee-${emp.id}`}>
                <Card className={`hover:shadow-md transition-shadow cursor-pointer ${!emp.is_active ? 'opacity-60' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {/* Avatar */}
                      <div className="flex-shrink-0">
                        {emp.photo ? (
                          <img src={emp.photo} alt={emp.name} className="w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <UserCircle className="w-7 h-7 text-blue-600" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-slate-800 truncate">{emp.name}</h3>
                          {!emp.is_active && (
                            <Badge className="bg-red-100 text-red-700 text-[10px]">
                              {language === 'en' ? 'Inactive' : 'নিষ্ক্রিয়'}
                            </Badge>
                          )}
                        </div>

                        <div className="flex items-center gap-1 text-sm text-slate-500 mb-1">
                          <Briefcase className="w-3.5 h-3.5" />
                          <span>{ROLE_LABELS[emp.role]?.[language] || emp.role}</span>
                        </div>

                        <div className="flex items-center gap-1 text-sm text-slate-500 mb-2">
                          <Phone className="w-3.5 h-3.5" />
                          <span>{emp.phone}</span>
                        </div>

                        <div className="flex flex-col gap-1.5 mt-1">
                          <div className="flex items-center gap-1 text-sm">
                            <DollarSign className="w-3.5 h-3.5 text-emerald-600" />
                            <span className="font-semibold text-emerald-700">
                              {formatCurrency(emp.monthly_salary || 0, language)}
                            </span>
                            <span className="text-slate-400 text-xs">/{language === 'en' ? 'mo' : 'মাস'}</span>
                          </div>
                          <div className="flex items-center flex-wrap gap-1.5">
                            {emp.total_advance_this_month > 0 && (
                              <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                                {language === 'en' ? 'Advance' : 'অগ্রিম'}: {formatCurrency(emp.total_advance_this_month, language)}
                              </Badge>
                            )}
                            {emp.remaining_salary > 0 ? (
                              <Badge className="bg-red-100 text-red-700 text-[10px]">
                                {language === 'en' ? 'Remaining' : 'বাকি'}: {formatCurrency(emp.remaining_salary, language)}
                              </Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                                {language === 'en' ? 'Fully Paid' : 'সম্পূর্ণ পরিশোধ'}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <UserX className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">
                {search
                  ? (language === 'en' ? 'No employees found' : 'কোন কর্মচারী পাওয়া যায়নি')
                  : (language === 'en' ? 'No employees yet' : 'এখনো কোন কর্মচারী নেই')}
              </h3>
              <p className="text-slate-400 mb-4">
                {language === 'en' ? 'Add your first employee to start tracking salaries and advances.' : 'বেতন ও অগ্রিম ট্র্যাক করতে প্রথম কর্মচারী যোগ করুন।'}
              </p>
              {!search && (
                <Link to={`${basePath}/employees/new`}>
                  <Button className="bg-blue-800 hover:bg-blue-900">
                    <Plus className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Add Employee' : 'কর্মচারী যোগ করুন'}
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        )}
      </div>
      </PremiumOverlay>
    </AdminLayout>
  );
}
